import React from 'react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Calendar, FileText, LogOut, User, Settings, Globe, CalendarDays } from 'lucide-react';
import { ViewType } from '@/types';
import { useAppContext } from '@/contexts/AppContext';
import { useTranslation } from '@/contexts/TranslationContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface SidebarProps {
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentView, onViewChange, onLogout }) => {
  const { currentUser } = useAppContext();
  const { t, language, setLanguage } = useTranslation();
  const isMobile = useIsMobile();

  const menuItems = [
    { id: 'calendar' as ViewType, label: 'Calendar', icon: CalendarDays },
    { id: 'customers' as ViewType, label: t('customers'), icon: Users },
    { id: 'reports' as ViewType, label: t('reports'), icon: Calendar },
    { id: 'profile' as ViewType, label: t('profile'), icon: User },
  ];

  // Only show admin menus for admin users
  if (currentUser?.role === 'admin') {
    menuItems.push({
      id: 'users' as ViewType,
      label: 'Sales Users',
      icon: Users
    });
    menuItems.push({
      id: 'settings' as ViewType,
      label: 'Settings',
      icon: Settings
    });
    menuItems.push({
      id: 'translations' as ViewType,
      label: t('translation.editor'),
      icon: Globe
    });
  }



  return (
    <div className={`${isMobile ? 'w-72' : 'w-64'} bg-white border-r border-gray-200 h-full flex flex-col`}>
      <div className={`${isMobile ? 'p-4' : 'p-6'} border-b border-gray-200`}>
        <h1 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-gray-900`}>
          Winery CRM
        </h1>
        <div className={`flex items-center ${isMobile ? 'mt-2' : 'mt-3'} space-x-3`}>
          <Avatar className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'}`}>
            <AvatarImage src={currentUser?.profilePicture} />
            <AvatarFallback className={isMobile ? 'text-sm' : ''}>
              {currentUser?.name?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className={`${isMobile ? 'text-sm' : 'text-sm'} font-medium text-gray-900 truncate`}>
              {currentUser?.name}
            </p>
            <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-gray-500 capitalize`}>
              {currentUser?.role}
            </p>
          </div>
        </div>
        
        {/* Language Selector */}
        <div className={`${isMobile ? 'mt-3' : 'mt-4'}`}>
          <div className="flex items-center space-x-2 mb-2">
            <Globe className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-700">{t('language')}</span>
          </div>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="el">Ελληνικά</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <nav className={`flex-1 ${isMobile ? 'p-3' : 'p-4'}`}>
        <div className="space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.id}
                variant={currentView === item.id ? 'default' : 'ghost'}
                className={cn(
                  'w-full justify-start',
                  isMobile ? 'h-12 text-base' : 'h-10',
                  currentView === item.id && 'bg-blue-600 text-white hover:bg-blue-700'
                )}
                onClick={() => onViewChange(item.id)}
              >
                <Icon className={`mr-2 ${isMobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
                {item.label}
              </Button>
            );
          })}
        </div>
      </nav>
      
      <div className={`${isMobile ? 'p-3' : 'p-4'} border-t border-gray-200`}>
        <Button
          variant="ghost"
          className={cn(
            'w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50',
            isMobile ? 'h-12 text-base' : 'h-10'
          )}
          onClick={onLogout}
        >
          <LogOut className={`mr-2 ${isMobile ? 'h-5 w-5' : 'h-4 w-4'}`} />
          {t('logout')}
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;